import { useEffect, useState } from 'react';
import Button from '../components/Button';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import { approverSettingsService, userService } from '../services/adminApi';

const APPROVER_MODULES = ['MAVIS-Leave', 'MAVIS-Leave-FWA', 'MAVIS-Claim', 'MAVIS-Appraisal', 'MAVIS-HR'];

interface ApproverRow {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_code: string;
  module_name: string;
  first_level_approver_name?: string;
  second_level_approver_name?: string;
  final_level_approver_name?: string;
}

// ADM-22: "Add Employees" assigns an approval chain to staff, unrelated to
// whether they have a system-user login — unlike Create User's employee
// picker, this one must NOT exclude employees who already have an account.
const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await userService.lookupEmployees(search, false);
  return res.data.employees.map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

/**
 * ADM-12: approver-role pickers (1st/2nd/final level) must only
 * offer users flagged "Is Approver" for this module — and, when a single target
 * employee is selected, only those whose Department Access scope covers that
 * employee. With multiple staff of possibly different departments selected, the
 * candidate list falls back to module-only scoping (no single scope to intersect).
 */
const fetchApproverOptions =
  (moduleName: string, staffIds: string[]) =>
  async (search: string): Promise<LookupOption[]> => {
    const employeeId = staffIds.length === 1 ? staffIds[0] : undefined;
    const res = await approverSettingsService.candidates(moduleName, employeeId);
    const options: LookupOption[] = res.data.employees.map((e: any) => ({
      value: e.id,
      label: `${e.full_name} (${e.code})`,
    }));
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  };

export default function ApproverSettings() {
  const [moduleName, setModuleName] = useState(APPROVER_MODULES[0]);
  const [rows, setRows] = useState<ApproverRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [staffIds, setStaffIds] = useState<string[]>([]);
  const [staffLabels, setStaffLabels] = useState<Record<string, string>>({});
  const [staffQuery, setStaffQuery] = useState('');
  const [firstLevel, setFirstLevel] = useState('');
  const [secondLevel, setSecondLevel] = useState('');
  const [finalLevel, setFinalLevel] = useState('');
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await approverSettingsService.list(moduleName);
      setRows(res.data.approverSettings || []);
    } finally {
      setLoading(false);
    }
  };

  const addStaffOption = (value: string, opt?: LookupOption) => {
    if (value && !staffIds.includes(value)) {
      setStaffIds([...staffIds, value]);
      if (opt) setStaffLabels((prev) => ({ ...prev, [value]: opt.label }));
    }
    setStaffQuery('');
  };

  const removeStaffOption = (id: string) => {
    setStaffIds(staffIds.filter((s) => s !== id));
    setStaffLabels((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleBatchAssignClick = () => {
    if (staffIds.length === 0) {
      alert('Select at least one employee');
      return;
    }
    setConfirmOpen(true);
  };

  const performBatchAssign = async () => {
    setSaving(true);
    try {
      await approverSettingsService.batchAssign({
        employeeIds: staffIds,
        moduleName,
        firstLevelApproverId: firstLevel || null,
        secondLevelApproverId: secondLevel || null,
        finalLevelApproverId: finalLevel || null,
      });
      setMessage(`Approver settings updated for ${staffIds.length} employee(s)`);
      setStaffIds([]);
      setStaffLabels({});
      setFirstLevel('');
      setSecondLevel('');
      setFinalLevel('');
      setConfirmOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<ApproverRow>[] = [
    { key: 'employee_code', header: 'Code', sortable: true },
    { key: 'employee_name', header: 'Employee', sortable: true },
    { key: 'first_level_approver_name', header: '1st Level', render: (r) => r.first_level_approver_name || '—' },
    { key: 'second_level_approver_name', header: '2nd Level', render: (r) => r.second_level_approver_name || '—' },
    { key: 'final_level_approver_name', header: 'Final Level', render: (r) => r.final_level_approver_name || '—' },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Approver Settings</h1>

      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="mb-4 max-w-xs">
        <Select
          label="Module"
          value={moduleName}
          onChange={(e) => setModuleName(e.target.value)}
          options={APPROVER_MODULES.map((m) => ({ value: m, label: m }))}
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
        <h2 className="font-semibold">Batch Assign</h2>
        <LookupSelect
          label="Add Employees"
          value=""
          displayValue={staffQuery}
          onChange={addStaffOption}
          fetchOptions={fetchEmployeeOptions}
          placeholder="Search and add employees..."
          required
        />
        <div className="flex flex-wrap gap-2">
          {staffIds.map((id) => (
            <span key={id} className="text-xs bg-gray-100 rounded px-2 py-1">
              {staffLabels[id] || id}
              <button onClick={() => removeStaffOption(id)} className="ml-2 text-red-600">
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <LookupSelect
            label="1st Level Approver"
            value={firstLevel}
            onChange={(v) => setFirstLevel(v)}
            fetchOptions={fetchApproverOptions(moduleName, staffIds)}
            placeholder="Search approver..."
          />
          <LookupSelect
            label="2nd Level Approver"
            value={secondLevel}
            onChange={(v) => setSecondLevel(v)}
            fetchOptions={fetchApproverOptions(moduleName, staffIds)}
            placeholder="Search approver..."
          />
          <LookupSelect
            label="Final Level Approver"
            value={finalLevel}
            onChange={(v) => setFinalLevel(v)}
            fetchOptions={fetchApproverOptions(moduleName, staffIds)}
            placeholder="Search approver..."
          />
        </div>
        <p className="text-xs text-gray-500">
          Approver dropdowns only list users flagged "Is Approver" for {moduleName}
          {staffIds.length === 1 ? ", scoped to the selected employee's department/designation" : ''}.
        </p>
        <Button onClick={handleBatchAssignClick}>Assign to Selected Employees</Button>
      </div>

      <Table
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="No approver settings yet"
        searchable
        searchPlaceholder="Search by code or employee..."
      />

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Assign Approvers"
        message={`Are you sure you want to update approver settings for ${staffIds.length} employee(s)?`}
        confirmLabel="Assign"
        variant="primary"
        loading={saving}
        onConfirm={performBatchAssign}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
